import React, { ReactElement, ReactNode } from "react";

class ReactChildren {
  public static hasChildren(children: ReactNode): boolean {
    return React.Children.count(children) > 0;
  }

  public static childrenCount(children: ReactNode): number {
    return React.Children.count(children);
  }

  public static countByType(children: ReactNode, type: string | Function): number {
    const childType = typeof type === "string" ? type : type.name;

    return React.Children.toArray(children).reduce((count: number, child) => {
      if (React.isValidElement(child) && this.getComponentType(child) === childType) {
        return count + 1;
      }
      return count;
    }, 0);
  }

  public static countByName(children: ReactNode, name: string): number {
    return React.Children.toArray(children).reduce((count: number, child) => {
      if (React.isValidElement(child)) {
        const childType = child.type as any;
        const childName = typeof childType === "string" ? childType : childType.name || childType.displayName;

        return childName === name ? count + 1 : count;
      }
      return count;
    }, 0);
  }

  public static isEmptyChildren(children: ReactNode): boolean {
    return React.Children.count(children) === 0;
  }
  public static onlyComponentChildren(children: ReactNode): ReactElement[] {
    return React.Children.toArray(children).filter(React.isValidElement);
  }

  public static deepFilter(children: ReactNode, filterFn: (child: React.ReactNode) => boolean): ReactNode[] {
    const result: ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) {
        if (filterFn(child as React.ReactNode)) result.push(child);
        return;
      }

      if (filterFn(child as React.ReactNode)) {
        result.push(React.cloneElement(child, {}, this.deepFilter(child.props.children, filterFn)));
      }
    });

    return result;
  }

  public static deepFind(
    children: ReactNode,
    findFn: (child: React.ReactNode) => boolean,
  ): React.ReactNode | undefined {
    let result: React.ReactNode | undefined;

    React.Children.forEach(children, (child) => {
      if (result) return;

      if (!React.isValidElement(child)) {
        if (findFn(child as React.ReactNode)) result = child as React.ReactNode;
        return;
      }

      if (findFn(child as React.ReactNode)) {
        result = child as React.ReactNode;
      } else {
        result = this.deepFind(child.props.children, findFn);
      }
    });

    return result;
  }

  public static deepForEach(children: ReactNode, forEachFn: (child: React.ReactNode) => void): void {
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) {
        forEachFn(child as React.ReactNode);
        return;
      }

      forEachFn(child as React.ReactNode);
      this.deepForEach(child.props.children, forEachFn);
    });
  }

  public static deepMap(
    children: ReactNode,
    mapFn: (child: React.ReactNode) => React.ReactNode,
  ): ReactNode[] | null | undefined {
    return React.Children.map(children, (child) =>
      React.isValidElement(child)
        ? React.cloneElement(child, {}, this.deepMap(child.props.children, mapFn))
        : mapFn(child as React.ReactNode),
    );
  }

  public static filter(children: ReactNode, filterFn: (child: React.ReactNode) => boolean): ReactNode[] {
    return React.Children.toArray(children).filter((child) => filterFn(child as React.ReactNode));
  }
  public static everyChild(children: ReactNode, predicate: (child: ReactNode) => boolean): boolean {
    return React.Children.toArray(children).every(predicate);
  }
  public static replaceChildren(
    children: ReactNode,
    oldChildType: string | Function,
    newChildren: ReactNode,
  ): ReactNode[] | undefined | null {
    return React.Children.map(children, (child) => {
      if (
        React.isValidElement(child) &&
        this.getComponentType(child) === (typeof oldChildType === "string" ? oldChildType : oldChildType.name)
      ) {
        return newChildren;
      }
      return child;
    });
  }

  public static getElementName(element: ReactElement): string | undefined {
    return element.type instanceof Function
      ? (element.type as any).displayName || element.type.name
      : (element.type as string);
  }

  public static groupByType(children: ReactNode): Record<string, ReactNode[]> {
    const groups: Record<string, ReactNode[]> = {};

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const name = this.getElementName(child) || "unknown";
        if (!groups[name]) groups[name] = [];
        groups[name].push(child);
      }
    });

    return groups;
  }

  public static isFirstChild(children: ReactNode, childToCheck: ReactNode): boolean {
    const childrenArray = React.Children.toArray(children);
    return childrenArray[0] === childToCheck;
  }

  public static isLastChild(children: ReactNode, childToCheck: ReactNode): boolean {
    const childrenArray = React.Children.toArray(children);
    return childrenArray[childrenArray.length - 1] === childToCheck;
  }

  public static isBetween(children: ReactNode, childToCheck: ReactNode): boolean {
    if (!React.isValidElement(childToCheck) && typeof childToCheck !== "string" && typeof childToCheck !== "number") {
      return false;
    }

    const childrenArray = React.Children.toArray(children);
    const index = childrenArray.indexOf(childToCheck as React.ReactChild);

    return index > 0 && index < childrenArray.length - 1;
  }

  public static hasComplexChildren(children: ReactNode): boolean {
    let hasComplexChildren = false;
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        hasComplexChildren = true;
      }
    });
    return hasComplexChildren;
  }

  public static onlyText(children: ReactNode): string[] {
    const textNodes: string[] = [];
    React.Children.forEach(children, (child) => {
      if (typeof child === "string") {
        textNodes.push(child);
      }
    });
    return textNodes;
  }

  public static onlyValid(children: ReactNode): ReactNode[] {
    return React.Children.toArray(children).filter(React.isValidElement);
  }

  public static flattenChildren(children: ReactNode): ReactNode[] {
    let result: ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.props.children) {
        result = result.concat(this.flattenChildren(child.props.children));
      } else {
        result.push(child);
      }
    });

    return result;
  }

  public static findParentType(children: ReactNode, parentType: string): ReactNode | null {
    let parent = null;
    const childrenArray = React.Children.toArray(children);

    for (let i = 0; i < childrenArray.length; i++) {
      let child = childrenArray[i];
      if (React.isValidElement(child)) {
        if (this.getElementName(child) === parentType) {
          parent = child;
          break;
        } else {
          if (child.props.children) {
            parent = this.findParentType(child.props.children, parentType);
            if (parent) break;
          }
        }
      }
    }

    return parent;
  }

  public static getSibling(children: ReactNode, childToCheck: ReactNode, direction: "next" | "prev"): ReactNode | null {
    const childrenArray = React.Children.toArray(children);
    const index = childrenArray.findIndex((child) => child === childToCheck);

    if (
      index < 0 ||
      (direction === "next" && index === childrenArray.length - 1) ||
      (direction === "prev" && index === 0)
    ) {
      return null;
    }

    return direction === "next" ? childrenArray[index + 1] : childrenArray[index - 1];
  }

  public static getChildProps(child: ReactNode): object | null {
    if (React.isValidElement(child)) {
      return child.props;
    }

    return null;
  }
  public static getComponentType(child: ReactNode): string | null {
    if (React.isValidElement(child)) {
      return typeof child.type === "string" ? child.type : child.type.name;
    }
    return null;
  }
  public static getChildByType(children: ReactNode, type: string | Function): ReactNode[] {
    return React.Children.toArray(children).filter(
      (child: ReactNode) => this.getComponentType(child) === (typeof type === "string" ? type : type.name),
    );
  }
  public static excludeChildByType(children: ReactNode, type: string | Function): ReactNode[] {
    return React.Children.toArray(children).filter(
      (child: ReactNode) => this.getComponentType(child) !== (typeof type === "string" ? type : type.name),
    );
  }
  public static getChildrenFromType(children: ReactNode, type: string | Function): ReactNode[] {
    const filteredChildren = this.getChildByType(children, type);
    return filteredChildren.reduce((result: ReactNode[], child: ReactNode) => {
      if (React.isValidElement(child)) {
        return result.concat(child.props.children);
      }
      return result;
    }, []);
  }
  public static getFirstNChildren(children: ReactNode, n: number): ReactNode[] {
    return React.Children.toArray(children).slice(0, n);
  }
  public static getLastNChildren(children: ReactNode, n: number): ReactNode[] {
    return React.Children.toArray(children).slice(-n);
  }
}

export { ReactChildren };
